/**
 * Context switcher data — Platform API wrappers for operators, clients, and
 * debt types, used by HubContext and SidebarContextSwitcher.
 */

import { fetchAllPages } from '@/apps/customers/services/_platformPaginate';
import { PlatformRequestError, platformErrorMessage, platformGet } from '@/apps/ingestion/services/_platformClient';
import type { Client, DebtType, Operator } from '@/types';

const CONTEXT_CLIENT_FIELDS = 'id,operator_id,display_name,legal_name,logo_url,status,tax_id,registration_number,metadata';
/** 5,000 rows at limit=100 — same ceiling as fetchAllPages. */
const CONTEXT_CLIENTS_MAX_PAGES = 50;

export interface ContextClientsResult {
  clients: Client[];
  /** True when FGA grants wildcard all-clients (`*`). */
  allClients: boolean;
}

interface OperatorListRow {
  id: string;
  name?: string | null;
  code?: string | null;
  legal_name?: string | null;
  legal_entity?: string | null;
  license_number?: string | null;
  registration_number?: string | null;
  tax_id?: string | null;
  headquarters_country?: string | null;
  default_currency?: string | null;
  timezone?: string | null;
  status?: string | null;
  workos_organization_id?: string | null;
  avatar_url?: string | null;
  avatar_color?: string | null;
  logo_url?: string | null;
}

interface ClientListRow {
  id: string;
  operator_id?: string | null;
  display_name?: string | null;
  legal_name?: string | null;
  logo_url?: string | null;
  status?: string | null;
  tax_id?: string | null;
  registration_number?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface DebtTypeListRow {
  id: string;
  client_id?: string | null;
  operator_id?: string | null;
  name?: string | null;
  description?: string | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function toOperator(row: OperatorListRow): Operator {
  return {
    ...row,
    name: row.name ?? '',
    active: row.status === 'active',
    workos_organization_id: row.workos_organization_id ?? null,
  } as Operator;
}

function toClient(row: ClientListRow): Client {
  return {
    id: row.id,
    operator_id: row.operator_id ?? '',
    display_name: row.display_name ?? '',
    legal_name: row.legal_name ?? '',
    logo_url: row.logo_url ?? null,
    status: (row.status as Client['status']) ?? null,
    tax_id: row.tax_id ?? null,
    registration_number: row.registration_number ?? null,
    metadata: row.metadata ?? {},
    ode_scope_key: null,
    branding: null,
    created_at: null,
    updated_at: null,
    workos_resource_id: null,
  };
}

function toDebtType(row: DebtTypeListRow): DebtType {
  return {
    id: row.id,
    client_id: row.client_id ?? '',
    operator_id: row.operator_id ?? '',
    name: row.name ?? '',
    description: row.description ?? null,
    status: (row.status as DebtType['status']) ?? 'active',
    metadata: row.metadata ?? {},
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
  };
}

/**
 * Active operators the signed-in user can switch into (hub org switcher).
 *
 * Sourced from WorkOS User Management organization memberships via the
 * self-scoped `/users/me/operators` endpoint, mapped through
 * `operators.workos_organization_id`. Not FGA resource roles.
 */
export async function fetchContextOperators(): Promise<Operator[]> {
  const rows = await fetchAllPages<OperatorListRow>('/platform/v1/users/me/operators', { sort: 'name' }, 'Failed to fetch operators');
  return rows.map(toOperator);
}

/**
 * Active and paused clients the signed-in user is authorised to see.
 *
 * Sourced from WorkOS FGA via the self-scoped `/users/me/clients` endpoint
 * (the wildcard "*" client grant = all operator clients) rather than the legacy
 * user_scope_links/FGA-driven `/clients` list. Authorisation is at client
 * granularity — debt types are implicitly visible when their client is.
 *
 * `allClients` comes from the envelope field (list expands `*` to concrete
 * rows, so Hub cannot infer all-access from ids alone).
 */
export async function fetchContextClients(): Promise<ContextClientsResult> {
  const path = '/platform/v1/users/me/clients';
  const query = {
    status: 'active,paused',
    fields: CONTEXT_CLIENT_FIELDS,
    sort: 'display_name',
  };
  const rows: ClientListRow[] = [];
  let cursor: string | null | undefined;
  let allClients = false;
  let pages = 0;

  do {
    const { data, error, response } = await platformGet(path, {
      params: { query: { ...query, limit: 100, ...(cursor ? { cursor } : {}) } },
    });
    if (error) {
      throw new PlatformRequestError(platformErrorMessage(error, 'Failed to fetch clients'), response?.status, error);
    }
    const envelope = (data ?? {}) as {
      data?: ClientListRow[];
      has_more?: boolean;
      next_cursor?: string | null;
      all_clients?: boolean;
    };
    if (pages === 0) {
      allClients = Boolean(envelope.all_clients);
    }
    rows.push(...(envelope.data ?? []));
    cursor = envelope.has_more ? envelope.next_cursor : null;
    if (++pages >= CONTEXT_CLIENTS_MAX_PAGES && cursor) {
      console.warn(`[fetchContextClients] hit page cap (${CONTEXT_CLIENTS_MAX_PAGES}); truncating`);
      break;
    }
  } while (cursor);

  return { clients: rows.map(toClient), allClients };
}

/** Active debt types for a single client. */
export async function fetchContextDebtTypes(clientId: string): Promise<DebtType[]> {
  const rows = await fetchAllPages<DebtTypeListRow>(
    '/platform/v1/debt-types',
    { client_id: clientId, status: 'active', sort: 'name' },
    'Failed to fetch debt types'
  );
  return rows.map(toDebtType);
}

/** Active debt types across multiple clients (sidebar hover panel). */
export async function fetchContextDebtTypesForClients(clientIds: string[]): Promise<DebtType[]> {
  if (clientIds.length === 0) return [];

  const rows = await fetchAllPages<DebtTypeListRow>(
    '/platform/v1/debt-types',
    { client_ids: clientIds.join(','), status: 'active', sort: 'name' },
    'Failed to fetch debt types'
  );
  return rows.map(toDebtType);
}

/** Extract a retryable HTTP status from a Platform API error, if present. */
export function platformErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as { status?: number; statusCode?: number };
  return candidate.status ?? candidate.statusCode;
}

export function platformErrorMessageWithFallback(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return platformErrorMessage(error, fallback);
}

/** Transient WorkOS / pool / gateway failures that bootstrap should retry. */
export function shouldRetryBootstrapError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const status = platformErrorStatus(error);
  if (typeof status === 'number') {
    return status >= 500;
  }

  const candidate = error as { code?: string; message?: string; details?: string };
  if (typeof candidate.code === 'string') {
    if (/^5\d\d$/.test(candidate.code)) return true;
    if (candidate.code.startsWith('08')) return true;
  }

  const text = `${error instanceof Error ? error.message : ''} ${candidate.message ?? ''} ${candidate.details ?? ''}`.toLowerCase();
  return (
    text.includes('identity provider') ||
    text.includes('failed to fetch') ||
    text.includes('networkerror') ||
    text.includes('network request failed') ||
    text.includes('load failed') ||
    text.includes('pool') ||
    text.includes('timeout') ||
    text.includes('timed out') ||
    text.includes('connection')
  );
}
